import { useCallback, useEffect, useMemo, useState } from "react";
import { LogOut, Loader2, CalendarDays, Users, FileText, Link2, Paperclip, ArrowRight, Sparkles } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { LearnLogin } from "@/components/learn/LearnAuth";
import { SessionCard, JoinButton, fmtDate } from "@/components/learn/SessionCard";
import { Assignments } from "@/components/learn/Assignments";
import { StartHere } from "@/components/learn/StartHere";
import { CertificateCard } from "@/components/learn/CertificateCard";
import { learn, studentToken, setStudentToken, sessionState, isPdf } from "@/lib/learnApi";
import { MaterialViewerProvider, useOpenMaterial } from "@/components/learn/MaterialViewer";
import { PATHWAY } from "@/data/offers";

const LINES = { llm: "Understand how LLMs work and find your first use case.", prompt: "Design, test and deploy reliable GenAI apps with RAG.",
  agentic: "Build, test and run real agentic systems, ending in a capstone." };
const UPGRADES = PATHWAY.filter((c) => LINES[c.key]).map((c) => ({ ...c, line: LINES[c.key] }));

const Upgrade = ({ owned }) => {
  const left = UPGRADES.filter((c) => !owned.includes(c.key));
  if (!left.length) return null;
  return (
    <section data-testid="learn-upgrade" className="rounded-2xl border border-teal-200 bg-gradient-to-br from-teal-50 to-white p-6">
      <p className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#0E9384]"><Sparkles className="h-4 w-4" /> Go further</p>
      <h2 className="font-display mt-1 text-[22px] font-bold text-[#0B1B33]">Turn the free classes into a project you can show</h2>
      <p className="mt-1 text-[14.5px] text-slate-700">Structured live cohorts with labs, checklists, project reviews and a completion certificate. No job promises, just real skills and evidence.</p>
      <div className="mt-4 grid gap-3 md:grid-cols-3">{left.map((c) => (
        <a key={c.key} href={`/courses/${c.key}`} className="group rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-[#0E9384]">
          <p className="font-semibold text-[#0B1B33]">{c.title}</p><p className="mt-1 text-[13.5px] text-slate-600">{c.line}</p>
          <p className="mt-3 flex items-center justify-between text-[14px]"><b className="text-[#0B1B33]">{c.fee}</b><span className="flex items-center gap-1 font-semibold text-[#0E9384]">See syllabus <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span></p>
        </a>))}</div>
      <p className="mt-4 text-[13.5px] text-slate-600">Not sure which fits? <a href="/go?diagnostic=1" className="font-semibold text-[#0E9384] underline">Take the free 5-minute readiness check</a>.</p>
    </section>
  );
};

const Materials = ({ items }) => {
  const open = useOpenMaterial();
  if (!items.length) return <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">Study material from your trainer will appear here.</p>;
  return (
    <div data-testid="learn-materials" className="grid gap-3 md:grid-cols-2">{items.map((m) => {
      const I = m.url ? Link2 : isPdf(m) ? FileText : Paperclip;
      return (
        <button key={m.id} data-testid={`material-${m.id}`} onClick={() => open(m)} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-[#0E9384]">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-teal-50"><I className="h-5 w-5 text-[#0E9384]" /></span>
          <span><span className="block font-semibold text-[#0B1B33]">{m.title}</span>
            <span className="text-[12.5px] text-slate-500">{m.url ? "Opens link" : isPdf(m) ? "View in portal" : `Download · ${m.filename}`}</span></span>
        </button>);
    })}</div>
  );
};

const NextUp = ({ sessions }) => {
  const next = sessions.find((s) => sessionState(s) === "live") || sessions.filter((s) => sessionState(s) === "upcoming").sort((a, b) => a.starts_at.localeCompare(b.starts_at))[0];
  if (!next) return null;
  const live = sessionState(next) === "live";
  return (
    <section data-testid="next-session-hero" className="grain relative overflow-hidden rounded-2xl bg-[#0B1B33] p-6 md:p-8">
      <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full border-[30px] border-[#0E9384]/50" />
      <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-teal-300">{live ? "Live now" : "Your next live session"}</p>
          <h2 className="font-display mt-2 text-[26px] font-bold leading-tight text-white md:text-[32px]">Session {next.n}: {next.title}</h2>
          <p className="mt-2 flex items-center gap-1.5 text-[15px] text-slate-300"><CalendarDays className="h-4 w-4" />{fmtDate(next.starts_at)} · live</p>
        </div>
        {next.zoom_link ? <JoinButton s={next} big /> : <p className="text-[14px] text-slate-300">Join link will appear here before the session.</p>}
      </div>
    </section>
  );
};

const Dashboard = ({ me, onLogout }) => {
  const courses = me?.courses || [];
  const [course, setCourse] = useState(courses.find((c) => c !== "free") || courses[0] || null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sessions");

  const load = useCallback(() => { if (course) learn.course(course).then(setData).catch(() => setData({ sessions: [], assignments: [], submissions: [], progress: {} })); }, [course]);
  useEffect(() => { setData(null); load(); }, [load]);
  const refreshCert = () => { learn.course(course).then((d) => setData((x) => ({ ...x, certificate: d?.certificate }))).catch(() => {}); };

  const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
  const progress = data?.progress || {};
  const pct = useMemo(() => {
    const total = sessions.reduce((a, s) => a + (s.checklist?.length || 0), 0);
    const done = sessions.reduce((a, s) => a + (progress[s.id]?.length || 0), 0);
    return total ? Math.round((done / total) * 100) : 0;
  }, [sessions, progress]);

  return (
    <div data-testid="learn-dashboard" className="min-h-screen bg-[#F7FAFC]">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-[1200px] items-center justify-between gap-3 px-5 lg:px-10">
          <Logo sub="LEARNING PORTAL" testId="learn-header-logo" />
          <div className="flex items-center gap-3"><span className="hidden text-[14px] text-slate-700 sm:block">Hi, {me?.name?.split(" ")[0]}</span>
            <button data-testid="learn-logout-btn" onClick={onLogout} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-[14px] text-slate-700 hover:bg-slate-100"><LogOut className="h-4 w-4" /> Sign out</button></div>
        </div>
      </header>
      <main className="mx-auto max-w-[1200px] space-y-6 px-5 py-8 lg:px-10">
        {!courses.length ? <p data-testid="learn-no-course" className="rounded-2xl bg-white p-10 text-center text-slate-700">You're not enrolled in a course yet. We'll email you as soon as access is added.</p> : <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#0E9384]">My course</p>
              <div className="mt-2 flex flex-wrap gap-2">{courses.map((c) => (
                <button key={c} data-testid={`learn-course-${c}`} onClick={() => setCourse(c)} className={`rounded-lg px-4 py-2 text-[14px] font-semibold transition-colors ${course === c ? "bg-[#0B1B33] text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}>{me.course_titles?.[c] || c}</button>
              ))}</div>
            </div>
            <div data-testid="learn-progress" className="w-full max-w-xs">
              <div className="flex justify-between text-[13px] text-slate-600"><span>Checklist progress</span><b className="text-[#0B1B33]">{pct}%</b></div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-gradient-to-r from-[#0E9384] to-[#2563EB] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            </div>
          </div>
          {!data ? <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#0E9384]" /> : <>
            {data.batch && <p data-testid="learn-batch" className="flex flex-wrap items-center gap-2 text-[14px] text-slate-700"><Users className="h-4 w-4 text-[#0E9384]" />Your batch: <b className="text-[#0B1B33]">{data.batch.name}</b> · {data.batch.label}</p>}
            <NextUp sessions={sessions} />
            {course === "free" ? <Upgrade owned={courses} /> : <CertificateCard course={course} cert={data.certificate} />}
            <div className="flex gap-2 overflow-x-auto border-b border-slate-200">
              {[["sessions", course === "free" ? `Live classes (${sessions.length})` : `Sessions (${sessions.length})`], ["materials", `Study material (${data.materials?.length || 0})`],
                ...(course === "free" && !data.assignments?.length ? [] : [["projects", `Projects (${data.assignments?.length || 0})`]]), ["start", "Start here"]].map(([k, l]) => (
                <button key={k} data-testid={`learn-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-3 text-[15px] font-semibold transition-colors ${tab === k ? "border-[#0E9384] text-[#0B1B33]" : "border-transparent text-slate-600 hover:text-[#0B1B33]"}`}>{l}</button>
              ))}
            </div>
            {tab === "sessions" && (sessions.length ? <div className="space-y-4">{sessions.map((s) => <SessionCard key={s.id} s={s} done={progress[s.id] || []} onProgress={(id, d) => { setData((x) => ({ ...x, progress: { ...x.progress, [id]: d } })); refreshCert(); }} />)}</div>
              : <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">{course === "free" ? "The next free live class will appear here with its join link." : "Sessions will appear here once the schedule is posted. Meanwhile, check the Start here tab."}</p>)}
            {tab === "materials" && <Materials items={data.materials || []} />}
            {tab === "projects" && <Assignments items={data.assignments || []} subs={data.submissions || []} onSubmitted={(s) => { setData((x) => ({ ...x, submissions: [...x.submissions, s] })); refreshCert(); }} />}
            {tab === "start" && <StartHere />}
          </>}
        </>}
      </main>
    </div>
  );
};

export default function Learn() {
  const [me, setMe] = useState(undefined);
  const check = useCallback(() => {
    if (!studentToken()) { setMe(null); return; }
    learn.me().then(setMe).catch(() => { setStudentToken(null); setMe(null); });
  }, []);
  useEffect(() => { check(); }, [check]);
  if (me === undefined) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#0E9384]" /></div>;
  if (!me) return <LearnLogin onLogin={check} />;
  return <MaterialViewerProvider><Dashboard me={me} onLogout={() => { setStudentToken(null); setMe(null); }} /></MaterialViewerProvider>;
}
